/**
 * DashboardPage — 数据总览页（移植自原型 design/Novel-admin/src/pages/dashboard.jsx）
 *
 * 还原原型 Dashboard 布局：KPI 卡片行 / 收入趋势 + 转化漏斗 / 留存矩阵 + App 营收占比 /
 * 热门书籍 TOP 6 + 风险告警。
 *
 * 接真（保持布局 / 视觉结构不变，只换数据源 + 币种符号）：
 *  - 取数：全部走 useDashboard()（真 API overview）—— KPI 标量 / revenue_by_app / top_books /
 *    alerts / 时序 / 拆解。useApps() 仅用于按 app_id join 取 App 图标 + 顶部 App 名（overview
 *    revenue_by_app 仅含 app_id/app_name，无 icon/color）。
 *  - 币种：金额符号由 dashboard.currency 决定（当前 USD→$，不再写死 €）。
 *  - 行为类（DAU / 漏斗 / 留存）：范围内无埋点就绪 App → 灰显「埋点接入中」，不显假数字；就绪后显真值，
 *    并注明「仅覆盖同意分析采集的用户」与未接入埋点（不计入）的 App；就绪但当期无数据显「当期无数据」。
 *  - 实时在线（ADM-DASH-S007）：张培裁决本轮不实现，overview 已不含 realtime 块 →
 *    原型「实时在线」卡降级为不显示，Row 2 收窄为 收入趋势 + 转化漏斗 两列。
 *  - 今日运营任务卡：张培裁决 NOT-in-scope（mock 无数据源）→ 移除该卡，右列「风险与告警」独占撑满。
 *  - app / setRoute props → 当前 App 由 useApps() + useAppScope().appId 推导；setRoute → useNavigate()。
 *  - window.fmtCountry / window.toast → ESM import；React.createElement(Icons[name]) → 类型化图标查表。
 *
 * 接真写动作（本轮 dashboard 域 PR）：
 *  - 时间段段控（24h/7d/30d/90d 闭集）+ 全局 AppSwitcher → useDashboard(range) 注入 query +
 *    进 queryKey ['dashboard', appScope, range]，切换重取（旧实现硬编码 all/30d 不联动）。
 *  - 刷新按钮 → queryClient.invalidateQueries(['dashboard']) 真重取（旧实现仅假 toast）。
 *  - 导出报表 → DashExportDialog（POST /export + GET /exports + 认证下载代理；ADM-DASH-S010）。
 *  - DateRangeButton 移除：契约 range 仅闭集枚举、无任意区间端点（见 plan §五）。
 *  - App 营收占比卡：scope=all 渲染占比卡，scope=app（契约 revenue_by_app 返 []）渲染虚线占位卡。
 */

import type { DashboardRange } from '@novel-admin/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppScope } from '~/app/app-scope-store';
import { type AppRow, useApps, useDashboard } from '~/data/queries';
import { currencySymbol, fmtCountry, fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { AppIcon } from '~/ui/AppIcon';
import { BookCover } from '~/ui/BookCover';
import { ChartSkeleton, KpiSkeleton, TableSkeleton } from '~/ui/Skeleton';
import { StatCard } from '~/ui/StatCard';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { Charts } from '~/ui/charts';
import { DashExportDialog } from './DashExportDialog';

export default function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appId = useAppScope((s) => s.appId);

  const [range, setRange] = useState<DashboardRange>('30d');
  const [revMode, setRevMode] = useState('stacked');
  const [exportOpen, setExportOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const { data: apps } = useApps();
  // dashboard 是 App 维度一等公民页 → 显式传顶栏 appScope + 段控 range 进 query（其它无参调用方
  // 默认 all/30d、与顶栏解耦，见 useDashboard 注释 F1）。
  // isError / hasData / isLoading：adversarial F2——初始 503/401/422 时不把零 KPI / 空图当真实数据
  // 渲染（见下方 overviewFailed / staleAfterRefreshFail 分流）。
  const {
    data: dashboard,
    isLoading: dashLoading,
    isError: dashError,
    hasData: dashHasData,
    refetch: refetchDashboard,
  } = useDashboard({ range, appScope: appId });

  // 取数失败且无任何成功数据（初始 503/401/422 / 无效 scope / 后端故障）→ 渲染显式错误态，
  // 绝不把 DASHBOARD_FALLBACK 的零 KPI / 空图当真实零活动展示（F2）。
  const overviewFailed = dashError;
  // 失败但仍有上次成功数据（refetch 失败、cache 留旧值）→ 保留旧值 + 顶部「刷新失败」标注（不静默）。
  const staleAfterRefreshFail = dashError && dashHasData;

  // 导出为写操作（POST /export 落 dashboard_exports + audit）→ 前端门控写角色闭集（root/admin/operator），
  // 与 server requireRole('root','admin','operator') 精确一致；viewer 只看记录、不能新建（A0 §鉴权口径）。
  const role = useAuthStore((s) => s.user?.role);
  const canExport = role === 'root' || role === 'admin' || role === 'operator';

  // 当前 App：appId==='all' → 聚合视图对象；否则取选中 App。
  const isAll = appId === 'all';
  const selectedApp = apps.find((a) => a.id === appId);
  // 兜底 platform 用 '未配置' 哨兵，不伪造 iOS：
  // - isAll：副标题走 apps 汇总分支、不渲染 app.platform（此处 platform 仅占位）；
  // - concrete scope 但 selectedApp 未就绪（apps 加载中 / 失效持久化 appId）：副标题会渲染 app.platform，
  //   伪造 iOS 会把假平台信号泄给运营 → 用 '未配置' + '未知 App' 诚实降级（与本 PR 止血口径一致）。
  const app: Pick<AppRow, 'name' | 'platform' | 'market'> = isAll
    ? { name: '全部 App', platform: '未配置', market: [] }
    : (selectedApp ?? { name: '未知 App', platform: '未配置', market: [] });

  // 实时在线（ADM-DASH-S007）本轮不实现 → 原 live tick / realtime 序列计算整段移除

  // 金额符号由真 DTO currency 决定（USD→$）
  const sym = currencySymbol(dashboard.currency);

  // App 图标 join：overview revenue_by_app 仅含 app_id/app_name → 按 app_id 反查 useApps() 取
  // icon/color；缺失（apps 未加载 / 无对应）时 AppIcon 用 app_name 首字兜底。
  const appById = useMemo(() => {
    const m: Record<string, AppRow> = {};
    for (const a of apps) m[a.id] = a;
    return m;
  }, [apps]);

  // 行为指标口径注记：sink 只收同意分析采集用户的事件；all 口径未就绪 App 不计入，须点名
  const behaviorNote = useMemo(() => {
    const missing = dashboard.behavior.missingAppIds.map((id) => appById[id]?.name ?? id);
    return missing.length > 0 && isAll
      ? `仅覆盖同意分析采集的用户 · 未接入埋点（不计入）：${missing.join('、')}`
      : '仅覆盖同意分析采集的用户';
  }, [dashboard.behavior.missingAppIds, appById, isAll]);
  const funnelHasData = (dashboard.funnel[0]?.value ?? 0) > 0;

  // App 营收占比：真值 revenue_by_app（已 minor→major）；合计用于副标题展示。
  const revenueByApp = dashboard.revenueByApp;
  const totalRev = revenueByApp.reduce((s, a) => s + a.revenue, 0);

  // 热门书籍：真值 top_books（按后端排序透传，取前 6）。
  const topBooks = dashboard.topBooks.slice(0, 6);

  // 收入趋势合计（真值，单位 major）→ 副标题展示（替代原硬编码 €1.14M）。
  const revenueTotal = dashboard.revenueBreakdown.reduce((s, p) => s + p.iap + p.stripe, 0);

  // 收入趋势副标题窗口文案：按 range 动态映射，切换段控后与实际窗口一致（旧实现硬编码「最近 30 天」漂移）。
  const rangeWindowLabel: Record<DashboardRange, string> = {
    '24h': '近 24 小时',
    '7d': '近 7 天',
    '30d': '近 30 天',
    '90d': '近 90 天',
  };
  // 分桶粒度提示（设计稿 dashboard.jsx 80：24h 逐小时、其余逐日，对齐契约 revenue_trend §2.2）。
  const revenueBucketLabel = range === '24h' ? '逐小时分桶' : '逐日分桶';

  // 告警跳转：DTO alerts.items[].ref.{domain,resource_id} → 对应处理域路由。
  // 已知 domain 才返回目标路由（USER→用户域、CONTENT→书籍域）；未知 domain 返回 null →
  // 该条不可点（不杜撰路由，checklist #4：不编造契约外的跳转目标）。
  const alertRoute = (domain: string, resourceId: string): string | null => {
    switch (domain.toUpperCase()) {
      case 'USER':
        return `/users?id=${resourceId}`;
      case 'CONTENT':
        return `/books?bookId=${resourceId}`;
      default:
        return null;
    }
  };

  // 收入拆解：BreakdownRow（date/iap/stripe 均为 string|number）适配图表的
  // Record<string, string|number>[] 入参；具名 interface 缺字符串索引签名，结构实为一致，
  // 经 unknown 桥接到图表通用行类型。
  const revenueBreakdownRows = dashboard.revenueBreakdown as unknown as Array<
    Record<string, string | number>
  >;

  // F2：取数失败且无成功数据 → 显式错误态。保留 header（含段控 / App 切换 / 重试）让用户能调整 scope /
  // 重试，但 body 不渲染零 KPI / 空图兜底（那会把后端故障 / 无效 scope 伪装成真实零活动）。
  if (overviewFailed) {
    return (
      <div className="page">
        <div className="page-header">
          <div>
            <h1 className="page-title">数据总览</h1>
            <div className="page-sub" style={{ color: 'var(--neg)' }}>
              数据加载失败
            </div>
          </div>
          <div className="hstack" style={{ gap: 8 }}>
            <div className="seg">
              {(['24h', '7d', '30d', '90d'] as const).map((r) => (
                <button key={r} className={range === r ? 'on' : ''} onClick={() => setRange(r)}>
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div
          className="card"
          style={{
            padding: 40,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            minHeight: 280,
          }}
        >
          <Icons.IconAlert size={28} style={{ color: 'var(--neg)' }} />
          <div style={{ fontSize: 14, fontWeight: 500 }}>总览数据加载失败</div>
          <div
            style={{ fontSize: 12, color: 'var(--fg-muted)', textAlign: 'center', maxWidth: 360 }}
          >
            未能从服务端获取数据（后端故障或当前 App scope 无效）。此处不展示占位数字，请重试或切换
            App scope。
          </div>
          <button
            className="btn"
            disabled={dashLoading}
            onClick={() => {
              refetchDashboard();
            }}
          >
            <Icons.IconRefresh size={13} />
            {dashLoading ? '重试中…' : '重试'}
          </button>
        </div>
      </div>
    );
  }

  // 首屏加载（真 query isLoading、尚无任何成功数据）→ 仅 body 显骨架，header（段控 / App 切换 /
  // 刷新 / 导出 / 面包屑）始终先渲染：避免「整页早返回骨架」把页面 chrome（段控、导出、刷新按钮）也
  // 一并藏掉——data query 慢 / 挂起时操作员仍能切段控 / 切 App / 触发重取，不被困死在骨架屏。
  // 骨架与真实 body 同形同尺（KpiSkeleton + ChartSkeleton×2 + TableSkeleton，复刻 PageSkeleton
  // dashboard body），数据到达后无位移替换；后续 refetch 保留旧值 + 顶部标注（staleAfterRefreshFail）。
  const bodyLoading = dashLoading && !dashHasData;

  return (
    // page-enter：数据加载完成后内容淡入（复用原型 .page-enter fade-in，与骨架屏无位移衔接）
    <div className="page page-enter">
      {/* 刷新失败但保留上次成功数据 → 顶部标注（F2：不静默把旧值当最新，也不退回零数据）*/}
      {staleAfterRefreshFail && (
        <div
          className="card"
          style={{
            padding: '8px 12px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--neg-soft)',
            color: 'var(--neg)',
            fontSize: 12,
          }}
        >
          <Icons.IconAlert size={14} />
          <span>刷新失败，下方为上次成功加载的数据</span>
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: 'auto' }}
            disabled={dashLoading}
            onClick={() => {
              refetchDashboard();
            }}
          >
            重试
          </button>
        </div>
      )}
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">数据总览</h1>
          <div className="page-sub">
            {isAll
              ? apps?.length
                ? `${apps.length} 个 App · ${new Set(apps.flatMap((a) => a.market)).size} 个地区 · ${new Set(apps.flatMap((a) => a.lang ?? [])).size} 种语言`
                : '全部 App'
              : `${app.name} · ${app.platform} · ${app.market.map((m) => fmtCountry(m)).join(' · ')}`}
            {/* 「● 实时」仅在有成功数据时展示——加载中 / 错误态下展示会暗示「实时真值」误导（F2）*/}
            {!dashLoading && !dashError && (
              <span
                style={{
                  marginLeft: 10,
                  padding: '1px 6px',
                  background: 'var(--pos-soft)',
                  color: 'var(--pos)',
                  borderRadius: 3,
                  fontSize: 11,
                }}
              >
                ● 实时
              </span>
            )}
          </div>
        </div>
        <div className="hstack" style={{ gap: 8 }}>
          <div className="seg">
            {(['24h', '7d', '30d', '90d'] as const).map((r) => (
              <button key={r} className={range === r ? 'on' : ''} onClick={() => setRange(r)}>
                {r}
              </button>
            ))}
          </div>
          {/* DateRangeButton 移除：契约 range 仅闭集枚举 24h/7d/30d/90d、无任意区间端点（plan §五）*/}
          <button
            className="btn btn-ghost btn-icon"
            title="刷新"
            disabled={refreshing}
            onClick={async () => {
              // 真重取并对账：refetch 当前 active ['dashboard', appScope, range] query，按结果发 toast——
              // 失败（overview 503/401 等）只发 neg toast，不无条件假成功（adversarial F2）。
              // refetchQueries 不 throw query error → 完成后显式查 query state 判成败。
              setRefreshing(true);
              const key = ['dashboard', appId, range];
              try {
                await queryClient.refetchQueries({ queryKey: ['dashboard'], type: 'active' });
                // TanStack Query v5：已有缓存数据的 query 重取失败时 status 仍是 'success'（保留旧 data），
                // 失败信号在 error + errorUpdatedAt。故判「最近一次结果是 error」= error 非空且
                // errorUpdatedAt >= dataUpdatedAt（refetch 后才更可靠地反映本次重取成败）。
                const s = queryClient.getQueryState(key);
                const failed = !!s?.error && s.errorUpdatedAt >= s.dataUpdatedAt;
                if (failed) {
                  toast('刷新失败，请重试', { tone: 'neg', icon: 'IconAlert' });
                } else {
                  toast('数据已刷新', { tone: 'pos', icon: 'IconRefresh' });
                }
              } catch {
                toast('刷新失败，请重试', { tone: 'neg', icon: 'IconAlert' });
              } finally {
                setRefreshing(false);
              }
            }}
          >
            <Icons.IconRefresh size={14} />
          </button>
          <button className="btn" onClick={() => setExportOpen(true)}>
            <Icons.IconDownload size={13} />
            导出报表
          </button>
        </div>
      </div>

      {/* body：首屏无数据时显骨架（与真实布局同形），否则渲染真实数据区。header 在上方已恒渲染 */}
      {bodyLoading ? (
        <>
          <KpiSkeleton count={5} />
          <div
            style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 12 }}
          >
            <ChartSkeleton height={220} />
            <ChartSkeleton height={220} title={100} />
          </div>
          <TableSkeleton rows={6} cols={5} />
        </>
      ) : (
        <>
          {dashboard.financialCoverage?.data_as_of && (
            <div className="muted" role="status">
              数据截至 {dashboard.financialCoverage.data_as_of} · 收入窗口{' '}
              {dashboard.financialCoverage.income_window?.start} —{' '}
              {dashboard.financialCoverage.income_window?.end}
            </div>
          )}
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
            <StatCard
              label="累计收入"
              value={
                dashboard.kpi.totalRevenue.pending
                  ? '收入数据准备中'
                  : dashboard.kpi.totalRevenue.value
              }
              delta={
                dashboard.kpi.totalRevenue.pending
                  ? undefined
                  : (dashboard.kpi.totalRevenue.deltaPct ?? undefined)
              }
              spark={dashboard.kpi.totalRevenue.pending ? undefined : dashboard.revenue30d}
              sparkColor="var(--chart-1)"
              format={(v) => (typeof v === 'number' ? `${sym}${v.toLocaleString()}` : String(v))}
              hint="本周期内净充值收入（Σ 充值 − Σ 退款）"
            />
            <StatCard
              label="付费用户数"
              value={dashboard.kpi.payingUsers.value}
              delta={dashboard.kpi.payingUsers.deltaPct ?? undefined}
              spark={dashboard.users30d}
              sparkColor="var(--chart-2)"
              format={(v) => v.toLocaleString()}
            />
            {/* DAU：行为埋点未就绪（PENDING）→ 灰显占位「埋点接入中」，不显假数字；就绪后显真值 */}
            {dashboard.kpi.dau.pending ? (
              <StatCard
                label="DAU"
                value="埋点接入中"
                sub="行为埋点接入后启用"
                format={(v) => String(v)}
              />
            ) : (
              <StatCard
                label="DAU"
                value={dashboard.kpi.dau.value}
                sub="昨日 · 仅同意分析采集用户"
                delta={dashboard.kpi.dau.deltaPct ?? undefined}
                spark={dashboard.dau30d}
                sparkColor="var(--chart-3)"
              />
            )}
            <StatCard
              label="ARPPU"
              value={dashboard.kpi.arppu.value ?? '—'}
              delta={
                dashboard.kpi.arppu.value === null
                  ? undefined
                  : (dashboard.kpi.arppu.deltaPct ?? undefined)
              }
              spark={dashboard.kpi.arppu.value === null ? undefined : dashboard.users30d}
              sparkColor="var(--chart-4)"
              format={(v) =>
                typeof v === 'number'
                  ? `${currencySymbol(dashboard.kpi.arppu.currency)}${v.toFixed(2)}`
                  : String(v)
              }
            />
            <StatCard
              label="被动退款率"
              value={
                dashboard.kpi.totalRevenue.pending
                  ? '收入数据准备中'
                  : (dashboard.kpi.passiveRefundRate.value ?? '—')
              }
              delta={
                dashboard.kpi.totalRevenue.pending
                  ? undefined
                  : (dashboard.kpi.passiveRefundRate.deltaPp ?? undefined)
              }
              spark={dashboard.kpi.totalRevenue.pending ? undefined : dashboard.refund30d}
              sparkColor="var(--chart-5)"
              format={(v) => (typeof v === 'number' ? `${v.toFixed(1)}%` : String(v))}
            />
          </div>

          {/* Row 2 — Revenue + Funnel（Realtime 卡片 ADM-DASH-S007 本轮不实现已移除，从 3 列收为 2 列）*/}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
              gap: 12,
            }}
          >
            {/* Revenue */}
            <div className="card" style={{ padding: 14, minWidth: 0 }}>
              <div className="hstack" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13.5 }}>收入趋势</div>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 11.5 }}>
                    按支付渠道拆分 · {rangeWindowLabel[range]} · {revenueBucketLabel} ·{' '}
                    {dashboard.kpi.totalRevenue.pending
                      ? '收入数据准备中'
                      : `${sym}${revenueTotal >= 1_000_000 ? `${(revenueTotal / 1_000_000).toFixed(2)}M` : Math.round(revenueTotal).toLocaleString()}`}
                  </div>
                </div>
                <div className="hstack" style={{ gap: 6 }}>
                  <div className="seg">
                    <button
                      className={revMode === 'stacked' ? 'on' : ''}
                      onClick={() => setRevMode('stacked')}
                    >
                      堆叠
                    </button>
                    <button
                      className={revMode === 'trend' ? 'on' : ''}
                      onClick={() => setRevMode('trend')}
                    >
                      趋势
                    </button>
                  </div>
                  <div
                    className="hstack"
                    style={{ gap: 10, fontSize: 11, color: 'var(--fg-muted)' }}
                  >
                    <span className="hstack" style={{ gap: 4 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          background: 'var(--chart-1)',
                        }}
                      />
                      IAP
                    </span>
                    <span className="hstack" style={{ gap: 4 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          background: 'var(--chart-2)',
                        }}
                      />
                      Stripe
                    </span>
                  </div>
                </div>
              </div>
              {dashboard.kpi.totalRevenue.pending ? (
                <div
                  className="muted"
                  style={{ minHeight: 210, display: 'grid', placeItems: 'center' }}
                >
                  收入数据准备中
                </div>
              ) : revMode === 'stacked' ? (
                <Charts.StackedBarChart
                  data={revenueBreakdownRows}
                  keys={['iap', 'stripe']}
                  colors={['var(--chart-1)', 'var(--chart-2)']}
                  height={210}
                />
              ) : (
                <Charts.AreaChart
                  data={revenueBreakdownRows}
                  height={210}
                  series={[
                    { key: 'iap', color: 'var(--chart-1)', label: 'IAP' },
                    { key: 'stripe', color: 'var(--chart-2)', label: 'Stripe' },
                  ]}
                  yFormat={(v) => `${sym}${Math.round(v).toLocaleString()}`}
                />
              )}
            </div>

            {/* 实时在线（ADM-DASH-S007）本轮不实现 → overview 无 realtime 块，整张卡片不渲染（降级为不显示）*/}

            {/* Funnel */}
            <div className="card" style={{ padding: 14, minWidth: 0 }}>
              <div className="hstack" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13.5 }}>转化漏斗</div>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 11.5 }}>打开 → 阅读 → 充值</div>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => toast('漏斗详情视图 · 待接入', { tone: 'info' })}
                >
                  详情
                  <Icons.IconChevRight size={11} />
                </button>
              </div>
              {funnelHasData ? (
                <Charts.Funnel data={dashboard.funnel} />
              ) : (
                // 漏斗依赖行为埋点（ADM-DASH-S005）：未就绪 → 接入中；就绪但当期无打开 → 无数据（均不显假数据）
                <div
                  style={{
                    height: 168,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--fg-faint)',
                    fontSize: 12,
                  }}
                >
                  {dashboard.behavior.pending
                    ? '转化漏斗 · 行为埋点接入中'
                    : '转化漏斗 · 当期无数据'}
                </div>
              )}
              {/* 就绪即注明覆盖口径（含当期无数据时）：all 口径须点名未计入的 App（A0 §1.3）*/}
              {!dashboard.behavior.pending && (
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--fg-muted)' }}>
                  {behaviorNote}
                </div>
              )}
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: '1px dashed var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11.5,
                }}
              >
                <span style={{ color: 'var(--fg-muted)' }}>整体转化</span>
                <span className="mono strong">
                  {dashboard.funnelOverallRate !== null
                    ? `${dashboard.funnelOverallRate.toFixed(2)}%`
                    : dashboard.behavior.pending
                      ? '埋点接入中'
                      : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Row 3 — Retention + Apps breakdown */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
              gap: 12,
            }}
          >
            {/* Retention */}
            <div className="card" style={{ padding: 14, minWidth: 0 }}>
              <div className="hstack" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13.5 }}>留存矩阵</div>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 11.5 }}>
                    按周 cohort · D0 至 D9 留存率 · 埋点 app_open 口径
                  </div>
                </div>
                <div className="hstack" style={{ gap: 10 }}>
                  <div
                    className="hstack"
                    style={{ gap: 6, fontSize: 11, color: 'var(--fg-muted)' }}
                  >
                    <span>低</span>
                    <div
                      style={{
                        width: 96,
                        height: 10,
                        borderRadius: 2,
                        background: 'linear-gradient(90deg, var(--bg-muted), var(--accent))',
                      }}
                    />
                    <span>高</span>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    title="导出留存矩阵 CSV"
                    onClick={() =>
                      toast('正在导出留存矩阵 CSV', { tone: 'info', icon: 'IconDownload' })
                    }
                  >
                    <Icons.IconDownload size={11} />
                  </button>
                </div>
              </div>
              {dashboard.retention.length > 0 ? (
                <>
                  <Charts.RetentionHeatmap data={dashboard.retention} />
                  <div style={{ marginTop: 10, fontSize: 11, color: 'var(--fg-muted)' }}>
                    按周 cohort 的 D0–D9 留存率热力图 · {behaviorNote}
                  </div>
                </>
              ) : (
                // 留存依赖行为埋点（ADM-DASH-S006）：未就绪 → 接入中；就绪但无完整周 cohort → 暂无（均不显假数据）
                <div
                  style={{
                    minHeight: 168,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--fg-faint)',
                    fontSize: 12,
                  }}
                >
                  {dashboard.behavior.pending
                    ? '留存矩阵 · 行为埋点接入中'
                    : '留存矩阵 · 暂无完整周 cohort'}
                </div>
              )}
              {dashboard.retention.length === 0 && !dashboard.behavior.pending && (
                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--fg-muted)' }}>
                  {behaviorNote}
                </div>
              )}
            </div>

            {/* App breakdown · 仅 app_scope=all 时展示（S004）：契约 §1.2 scope=app 时 revenue_by_app
            返 [] → 单 App 视图渲染虚线占位卡（文案同设计稿 dashboard.jsx 201-205）*/}
            {isAll ? (
              <div className="card" style={{ padding: 14, minWidth: 0 }}>
                <div
                  className="hstack"
                  style={{ justifyContent: 'space-between', marginBottom: 10 }}
                >
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13.5 }}>App 营收占比</div>
                    <div style={{ color: 'var(--fg-muted)', fontSize: 11.5 }}>
                      {dashboard.kpi.totalRevenue.pending
                        ? '收入数据准备中'
                        : `本周期 · 总额 ${sym}${Math.round(totalRev).toLocaleString()}`}
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate('/apps')}>
                    所有 App
                    <Icons.IconChevRight size={11} />
                  </button>
                </div>
                {dashboard.kpi.totalRevenue.pending ? (
                  <div
                    className="muted"
                    style={{ minHeight: 130, display: 'grid', placeItems: 'center' }}
                  >
                    收入数据准备中
                  </div>
                ) : (
                  <div className="hstack" style={{ gap: 14, alignItems: 'flex-start' }}>
                    <Charts.Donut
                      size={130}
                      thickness={16}
                      data={revenueByApp.map((a, i) => ({
                        value: a.revenue,
                        color: `var(--chart-${(i % 6) + 1})`,
                      }))}
                    />
                    <div className="vstack" style={{ gap: 4, flex: 1 }}>
                      {revenueByApp.slice(0, 5).map((a, i) => (
                        <div key={a.appId} className="hstack" style={{ gap: 8, fontSize: 11.5 }}>
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: `var(--chart-${(i % 6) + 1})`,
                            }}
                          />
                          {/* icon/color 来自 useApps() join app_id；缺失时用 app_name 首字兜底 */}
                          <AppIcon
                            app={appById[a.appId]}
                            label={appById[a.appId] ? undefined : a.appName.slice(0, 1)}
                            size={{ box: 18, font: 8.5, radius: 4 }}
                          />
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {a.appName}
                          </span>
                          <span className="mono">
                            {sym}
                            {Math.round(a.revenue / 1000)}k
                          </span>
                          <span
                            className="mono"
                            style={{ color: 'var(--fg-muted)', width: 38, textAlign: 'right' }}
                          >
                            {a.sharePct.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                      {revenueByApp.length > 5 && (
                        <div style={{ fontSize: 11, color: 'var(--fg-faint)', paddingLeft: 16 }}>
                          +{revenueByApp.length - 5} 个 App
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // scope=app：营收占比仅在「全部 App」视图下有意义（契约 revenue_by_app 返 []）→ 虚线占位卡
              <div
                className="card"
                style={{
                  padding: 14,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  borderStyle: 'dashed',
                }}
              >
                <Icons.IconChart size={18} style={{ color: 'var(--fg-faint)' }} />
                <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  App 营收占比仅在「全部 App」视图下展示
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                  当前为单 App scope · 切换顶部 App 选择器查看
                </div>
              </div>
            )}
          </div>

          {/* Row 4 — Top books + Alerts */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
              gap: 12,
            }}
          >
            {/* Top books */}
            <div className="card" style={{ minWidth: 0, overflow: 'hidden' }}>
              <div
                className="hstack"
                style={{ justifyContent: 'space-between', padding: '12px 14px' }}
              >
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13.5 }}>热门书籍 TOP 6</div>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 11.5 }}>
                    按窗口内活跃阅读用户数排序
                  </div>
                  <div style={{ color: 'var(--fg-muted)', fontSize: 10.5, marginTop: 2 }}>
                    已注销用户经匿名化折叠为 1 · 名次与人数均为观测近似，非结算
                  </div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/books')}>
                  查看全部
                  <Icons.IconChevRight size={11} />
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <Table className="tbl" style={{ tableLayout: 'auto', minWidth: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>#</th>
                      <th>书籍</th>
                      <th>分类</th>
                      <th
                        className="num"
                        title="窗口内活跃阅读 UV · 已注销用户经匿名化折叠为 1，观测近似，非结算"
                      >
                        阅读用户
                      </th>
                      <th
                        className="num"
                        title="窗口内去重付费用户 · 已注销用户经匿名化折叠为 1，观测近似，非结算"
                      >
                        付费用户
                      </th>
                      <th className="num">付费章节</th>
                      <th className="num">解锁收入 (Coin)</th>
                      <th style={{ width: 92 }}>趋势</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topBooks.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          style={{
                            textAlign: 'center',
                            color: 'var(--fg-faint)',
                            padding: '24px 0',
                          }}
                        >
                          暂无数据
                        </td>
                      </tr>
                    )}
                    {topBooks.map((b, i) => (
                      <tr
                        key={b.novelId}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/books?bookId=${b.novelId}`)}
                      >
                        <td className="mono muted">{String(i + 1).padStart(2, '0')}</td>
                        <td style={{ minWidth: 180, maxWidth: 220 }}>
                          <div className="hstack" style={{ gap: 8 }}>
                            {/* 封面优先真图 cover_url；缺失时 BookCover 渐变占位兜底 */}
                            {b.coverUrl ? (
                              <img
                                src={b.coverUrl}
                                alt=""
                                width={26}
                                height={34}
                                style={{ borderRadius: 3, objectFit: 'cover', flexShrink: 0 }}
                              />
                            ) : (
                              <BookCover width={26} height={34} radius={3} />
                            )}
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: 500,
                                  fontSize: 12.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {b.title}
                              </div>
                              {b.author && (
                                <div
                                  className="mono"
                                  style={{
                                    fontSize: 10,
                                    color: 'var(--fg-faint)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {b.author}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="chip">{b.categoryName}</span>
                        </td>
                        {/* readers = range 窗口内活跃阅读 UV（契约恒 integer）；
                            旧 admin-api 仍返 null 时灰显「—」，不渲染成 0 */}
                        <td className="num">
                          {b.readers === null ? (
                            <span style={{ color: 'var(--fg-faint)' }}>—</span>
                          ) : (
                            b.readers.toLocaleString()
                          )}
                        </td>
                        <td className="num">{b.payers.toLocaleString()}</td>
                        <td className="num">{b.paidChapters.toLocaleString()}</td>
                        {/* 收入：章节解锁 Coin 口径真值（非法币）*/}
                        <td className="num mono">{Math.round(b.revenue).toLocaleString()}</td>
                        <td>
                          {/* 趋势 sparkline 依赖逐日 readers 序列（行为埋点未就绪）→ 占位 */}
                          <span style={{ color: 'var(--fg-faint)', fontSize: 11 }}>—</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Alerts（今日运营任务卡张培裁决 NOT-in-scope 已移除，右列改由风险与告警卡独占撑满）*/}
            <div className="vstack" style={{ gap: 12, minWidth: 0 }}>
              <div className="card" style={{ padding: 14 }}>
                <div
                  className="hstack"
                  style={{ justifyContent: 'space-between', marginBottom: 10 }}
                >
                  <div style={{ fontWeight: 500, fontSize: 13.5 }}>风险与告警</div>
                  {dashboard.alerts.pendingCount > 0 && (
                    <span className="chip chip-warn">{dashboard.alerts.pendingCount} 待处理</span>
                  )}
                </div>
                <div className="vstack" style={{ gap: 8 }}>
                  {dashboard.alerts.items.length === 0 ? (
                    <div
                      style={{
                        padding: '24px 0',
                        textAlign: 'center',
                        color: 'var(--fg-faint)',
                        fontSize: 12,
                      }}
                    >
                      暂无风险与告警
                    </div>
                  ) : (
                    dashboard.alerts.items.map((a, i) => {
                      const AlertIcon = Icons[a.icon];
                      // ref.domain 可路由 → 整条可点跳转对应处理域；不可路由（未知 domain）→ 纯展示
                      const route = alertRoute(a.ref.domain, a.ref.resourceId);
                      return (
                        <div
                          key={i}
                          className="hstack"
                          onClick={route ? () => navigate(route) : undefined}
                          style={{
                            gap: 10,
                            alignItems: 'flex-start',
                            padding: '8px 10px',
                            border: '1px solid var(--border)',
                            borderRadius: 6,
                            background: a.tone === 'neg' ? 'var(--neg-soft)' : 'var(--bg-subtle)',
                            cursor: route ? 'pointer' : 'default',
                          }}
                        >
                          <div
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: 5,
                              background: 'var(--bg-elev)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              color: `var(--${a.tone})`,
                            }}
                          >
                            <AlertIcon size={12} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 500, marginBottom: 1 }}>
                              {a.title}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{a.sub}</div>
                          </div>
                          <div
                            className="mono"
                            style={{ fontSize: 10, color: 'var(--fg-faint)', whiteSpace: 'nowrap' }}
                          >
                            {fmtDateTime(a.time)}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 报表导出弹窗（ADM-DASH-S010）：当前 range / appScope 口径快照 */}
      {exportOpen && (
        <DashExportDialog
          range={range}
          appScope={appId}
          scopeLabel={app.name}
          canExport={canExport}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  );
}
